// src/pages/auth/VerifyEmail.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { useAuth } from '../../context/AuthContext';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const { verifyEmail, resendVerificationEmail, user } = useAuth();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');

    // If user is already verified, redirect immediately
    if (user?.email_verified) {
      setMessage('Your email is already verified.');
      setIsVerified(true);
      setTimeout(() => navigate('/dashboard'), 3000);
      return;
    }

    // If token exists in URL, verify it
    if (token) {
      handleVerifyToken(token);
    }
  }, [searchParams, user]);

  const handleVerifyToken = async (token) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await verifyEmail(token);

      if (result.success) {
        // Handle "already verified" or "just verified"
        setIsVerified(true);
        setMessage(
          result.already_verified
            ? 'Your email is already verified. You can login.'
            : 'Your email has been successfully verified.'
        );

        // Redirect after 3 seconds
        setTimeout(() => navigate('/dashboard'), 3000);
      } else {
        setError(result.error || 'Failed to verify email.');
      }
    } catch (err) {
      setError('Failed to verify email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!user?.email) return;

    setIsResending(true);
    setError('');
    try {
      const result = await resendVerificationEmail();
      if (result.success) {
        setMessage(result.message);
      } else {
        setError(result.error || 'Failed to resend verification email.');
      }
    } catch {
      setError('Failed to resend verification email.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-black flex items-center justify-center p-4">
      <Header />
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 shadow-2xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl mb-4">
              {isVerified ? (
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              )}
            </div>

            <h2 className="text-2xl font-bold text-white mb-2">
              {isVerified ? 'Email Verified!' : 'Verify Your Email'}
            </h2>

            <p className="text-gray-400">
              {isVerified
                ? message
                : 'Please verify your email address to continue.'}
            </p>
          </div>

          {isLoading && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-400">Verifying your email...</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                  <span className="text-white text-sm">!</span>
                </div>
                <span className="text-red-400">{error}</span>
              </div>
            </div>
          )}

          {!isVerified && !isLoading && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-blue-400 text-sm">
                  We've sent a verification email to <strong>{user?.email}</strong>.
                  Please check your inbox and click the verification link.
                </p>
              </div>

              <button
                onClick={handleResendVerification}
                disabled={isResending}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </div>
                ) : (
                  'Resend Verification Email'
                )}
              </button>

              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-xl transition-all duration-300"
              >
                Go to Dashboard
              </button>
            </div>
          )}

          {isVerified && (
            <div className="text-center mt-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold rounded-xl transition-all duration-300"
              >
                Continue to Dashboard
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Need help?{' '}
              <a href="mailto:support@diceace12.com" className="text-blue-400 hover:text-blue-300">
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
